import React, { useEffect, useState } from "react";
import {
    Button,
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
    Table
} from "reactstrap";
import { useFormik } from "formik";
import Select from "react-select";
import { useSelector } from "react-redux";
import { RootState } from "index";
import { customStyle } from "shared/CommonCSS";
import { queryKeyes } from "shared/queryKeys";
import { useQuery } from "react-query";
import { Get } from "VesselMaster/vesselMaster.hooks";
import apiGlobal from "global/api.global";
import { errorToast, successToast } from "Components/Toasts";
import Loading from "Components/Loading";
import { commonValidationMessages } from "Components/ValidationErrorMessages";
import * as Yup from "yup";
import ErrorTooltip from "Components/ErrorTooltip";
import FormValuesDebug from "utils/debugTools/FormValuesDebug";
import VesselDetailsHeader from "Components/VesselDetailsHeader";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Logo from '../Media/LogoLogin.png';
import Message from "Components/Message";

interface LubeOilROBDataType {
    activeTab: string
}

const LubeOilROBData = ({ activeTab }: LubeOilROBDataType) => {
    // Get vessel name
    const { Vessels, VesselID } = useSelector((state: RootState) => state.Reporting);
    const vesselRecord = Vessels.find((v: any) => v.id === VesselID);
    const vesselName = vesselRecord?.vessel_name || "Unknown Vessel";

    // Get vessel lube oil options
    const { data: lubeOilOptions, } = useQuery(
        [queryKeyes.vessel.VesselLubeOils.key, VesselID],
        async () => await Get(queryKeyes.vessel.VesselLubeOils.url(VesselID), VesselID),
        { enabled: true, staleTime: Infinity }
    );
    // STATE TO SIMULATE API LOADED DATA
    const [apiData, setApiData] = useState([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState(null);

    // FORMIK
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            vessel: VesselID,
            vesselLubeOilTypes: [],
            startDate: "",
            endDate: "",
        },
        validationSchema: Yup.object().shape({
            startDate: Yup.date().required(commonValidationMessages.required),
            endDate: Yup.date().required(commonValidationMessages.required),
            vesselLubeOilTypes: Yup.array()
                .min(1, commonValidationMessages.required)
                .required(commonValidationMessages.required),
        }),
        onSubmit: () => {
            setLoading(true);
            formik.setSubmitting(true);
            apiGlobal.post('reporting_lube_oil_rob/get_lube_oil_rob_between_date_range/', formik.values)
                .then((response) => {
                    setApiData(response.data);
                    setError("");
                    successToast("Lube Oil ROB Data loaded successfully");
                })
                .catch((error) => {
                    errorToast(error.response.data.message);
                    setError(error.response.data.message);
                }).finally(() => (
                    formik.setSubmitting(false),
                    setLoading(false)
                ));
        }
    });

    // FIND UNIQUE VESSEL_LUBE_OIL IDS
    const oilsInResponse = Array.from(
        new Set(
            apiData?.flatMap((row: any) =>
                row.lube_oils?.map((o: any) => o.vessel_lube_oil)
            )
        )
    );

    // MATCH IDS WITH LABEL OPTIONS
    const tableOilTypes = oilsInResponse
        .map((id) => lubeOilOptions?.find((opt: any) => opt.id === id))
        .filter(Boolean);

    const exportToExcel = async () => {
        const { startDate, endDate } = formik.values;

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Lube Oil ROB");

        // -------------------- MAIN HEADING --------------------
        const mainHeading = `Lube Oil ROB Data for ${vesselName} from (${startDate} to ${endDate})`;
        const mainHeaderRow = sheet.addRow([mainHeading]);

        // Calculate total columns: # + Report Name + (5 columns × number of oil types)
        const totalColumns = 2 + (tableOilTypes.length * 5);
        sheet.mergeCells(1, 1, 1, totalColumns);

        mainHeaderRow.eachCell((cell: ExcelJS.Cell) => {
            cell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "4472C4" },
            };
        });

        sheet.addRow([]);

        // -------------------- MAIN HEADER ROW (Oil Type Names) --------------------
        const mainHeader: any[] = ["#", "Report Name"];

        tableOilTypes.forEach((oil: any) => {
            mainHeader.push(oil.vessel_lube_oil_name);
            // Add empty cells for the colspan effect
            mainHeader.push("", "", "", "");
        });

        const mainHeaderRowNum = sheet.addRow(mainHeader).number;

        // Merge cells for each oil type name (5 columns each)
        let colIndex = 3; // Start after # and Report Name
        tableOilTypes.forEach(() => {
            sheet.mergeCells(mainHeaderRowNum, colIndex, mainHeaderRowNum, colIndex + 4);
            colIndex += 5;
        });

        // Style main header row
        sheet.getRow(mainHeaderRowNum).eachCell((cell: ExcelJS.Cell) => {
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "D9E1F2" },
            };
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
        });

        // -------------------- SUB HEADER ROW (CO, BO, DBK, ADJ, ROB) --------------------
        const subHeader: any[] = ["", ""];

        tableOilTypes.forEach(() => {
            subHeader.push("CO", "BO", "DBK", "ADJ", "ROB");
        });

        const subHeaderRow = sheet.addRow(subHeader);

        subHeaderRow.eachCell((cell: ExcelJS.Cell) => {
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "F9D0D5" },
            };
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
        });

        // -------------------- TABLE DATA --------------------
        apiData.forEach((row: any, index: number) => {
            const excelRow: any[] = [
                index + 1,
                `${row.report_name} (${new Date(row.report_time).toLocaleString()})`,
            ];

            tableOilTypes.forEach((oil: any) => {
                const match = row.lube_oils?.find(
                    (x: any) => x.vessel_lube_oil === oil.id
                );

                excelRow.push(
                    match?.consumed ?? "-",
                    match?.bunkered ?? "-",
                    match?.debunkered ?? "-",
                    match?.adjusted ?? "-",
                    match?.rob ?? "-"
                );
            });

            const addedRow = sheet.addRow(excelRow);

            addedRow.eachCell((cell: ExcelJS.Cell, colNum: number) => {
                // Left align first two columns, center the rest
                cell.alignment = {
                    horizontal: colNum <= 2 ? "left" : "center",
                    vertical: "middle"
                };

                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                };

                // Make ROB and ADJ columns bold
                // ROB columns are at positions: 7, 12, 17, 22, etc. (every 5th starting from 7)
                // ADJ columns are at positions: 6, 11, 16, 21, etc. (every 5th starting from 6)
                const isRobOrAdjColumn = colNum > 2 && ((colNum - 2) % 5 === 4 || (colNum - 2) % 5 === 0);
                if (isRobOrAdjColumn) {
                    cell.font = { bold: true };
                }

                // Alternating row colors
                if (index % 2 === 0) {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "F2F2F2" },
                    };
                }
            });
        });

        // -------------------- AUTO COLUMN WIDTHS --------------------
        sheet.columns.forEach((col: any, i: number) => {
            if (i === 0) {
                col.width = 8; // # column
            } else if (i === 1) {
                col.width = 40; // Report Name column
            } else {
                col.width = 12; // Data columns
            }
        });

        // -----------------------------------------------------
        //  FOOTER (TWO EMPTY ROWS + LOGO + TEXT)
        // -----------------------------------------------------
        sheet.addRow([]);
        sheet.addRow([]);

        // ---- Convert logo to base64 ----
        const logoResponse = await fetch(Logo);
        const logoBlob = await logoResponse.blob();
        const logoBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
        });

        const logoId = workbook.addImage({
            base64: (logoBase64 as string).split(",")[1],
            extension: "png",
        });

        // ---- Add footer row ----
        const footerRow = sheet.addRow(["", "Created by ecoSAIL Infotech"]);

        // Merge footer text across cells (columns 2 to 7)
        sheet.mergeCells(footerRow.number, 2, footerRow.number, 2);

        footerRow.getCell(2).alignment = {
            horizontal: "center",
            vertical: "middle",
        };
        footerRow.getCell(2).font = {
            italic: true,
            size: 11,
            color: { argb: "666666" },
        };

        // ---- Add logo on the left (column 1) ----
        sheet.addImage(logoId, {
            tl: { col: 0, row: footerRow.number - 1 },
            ext: { width: 20, height: 20 },
        });
        // -------------------- DOWNLOAD FILE --------------------
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `lube_oil_rob_${startDate}_${endDate}.xlsx`);
    };

    /**delete data when vessel changes */
    useEffect(() => {
        setApiData([]);
    }, [VesselID]);

    return (
        <Card>
            <CardBody>
                <Card>
                    <Row className="mb-2 p-2">
                        <Col sm={3}>
                            <h4 className="page_title pos-start mt-2">Lube  Oil ROB Data</h4>
                        </Col>
                        <Col sm={9}>
                            <VesselDetailsHeader />
                        </Col>
                    </Row>
                </Card>
                {lubeOilOptions && (<>

                    {/* FILTER FORM */}
                    <Form onSubmit={formik.handleSubmit}>
                        <Row className="align-items-end">
                            {/* Oil Type */}
                            <Col md="3">
                                <FormGroup>
                                    <Label >Select Lube Oil</Label>
                                    <Select
                                        options={lubeOilOptions.map((item: any) => ({
                                            value: item.id,
                                            label: item.vessel_lube_oil_name
                                        }))}
                                        inputId="vesselLubeOilTypes"
                                        isMulti
                                        styles={customStyle}
                                        onBlur={(e) => {
                                            formik.handleBlur(e)
                                            formik.setFieldTouched("vesselLubeOilTypes")
                                        }}
                                        onChange={(selected) =>
                                            formik.setFieldValue(
                                                "vesselLubeOilTypes",
                                                selected ? selected.map((s) => s.value) : []
                                            )
                                        }
                                        value={
                                            formik.values.vesselLubeOilTypes.map((id) => {
                                                const opt = lubeOilOptions?.find((o: any) => o.id === id);
                                                return opt ? { value: opt.id, label: opt.vessel_lube_oil_name } : null;
                                            }).filter(Boolean)
                                        }
                                    />
                                </FormGroup>
                                {formik.touched.vesselLubeOilTypes && formik.errors.vesselLubeOilTypes && activeTab === '2' && (
                                    <ErrorTooltip
                                        message={formik.errors.vesselLubeOilTypes}
                                        target={"vesselLubeOilTypes"}
                                        open={true}
                                    />
                                )}
                            </Col>
                            {/* Dates */}
                            <Col md="3">
                                <FormGroup>
                                    <Label className="asteric">Start Date</Label>
                                    <Input
                                        type="datetime-local"
                                        name="startDate"
                                        id={"startDate_lo"}
                                        value={formik.values.startDate}
                                        onChange={formik.handleChange}
                                        className="datetimepicker text-uppercase"
                                        onBlur={(e) => {
                                            formik.setFieldTouched("startDate")
                                            formik.handleBlur(e)
                                        }}
                                        max={"9999-12-31T00:00"}
                                    />
                                </FormGroup>
                                {formik.touched.startDate && formik.errors.startDate && activeTab === '2' && (
                                    <ErrorTooltip
                                        message={formik.errors.startDate}
                                        target={"startDate_lo"}
                                        open={true}
                                    />
                                )}
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label className="asteric">End Date</Label>
                                    <Input
                                        type="datetime-local"
                                        name="endDate"
                                        id="endDate_lo"
                                        value={formik.values.endDate}
                                        onChange={formik.handleChange}
                                        className="datetimepicker text-uppercase"
                                        onBlur={(e) => {
                                            formik.setFieldTouched("endDate")
                                            formik.handleBlur(e)
                                        }}
                                        max={"9999-12-31T00:00"}
                                    />
                                </FormGroup>
                                {formik.touched.endDate && formik.errors.endDate && activeTab === '2' && (
                                    <ErrorTooltip
                                        message={formik.errors.endDate}
                                        target={"endDate_lo"}
                                        open={true}
                                    />
                                )}
                            </Col>
                            <Col md="3" className="d-flex align-items-end">
                                <Button
                                    type="submit"
                                    color="primary"
                                    size="md"
                                    className="mb-3"
                                    disabled={formik.isSubmitting || !formik.isValid}
                                >
                                    Generate
                                </Button>
                            </Col>
                            <FormValuesDebug values={[formik.values, formik.errors, formik.touched]} />
                        </Row>
                    </Form>
                    {loading && <Loading message="Loading Data!" />}
                    {(error && !loading) && <Message message={error} type="danger" />}
                    {apiData.length > 0 && (
                        <>
                            <Row className="mt-4">
                                <div style={{ overflowX: "auto" }}>
                                    <Table bordered style={{ minWidth: "100%" }}>
                                        <thead className="table-light">
                                            {/* MAIN HEADER */}
                                            <tr>
                                                <th style={{ minWidth: "50px" }}>#</th>
                                                <th className="text-center" style={{ minWidth: "250px" }}>Report Name</th>

                                                {tableOilTypes.map((oil) => (
                                                    <th key={oil.id} className="text-center" colSpan={5}>
                                                        {oil.vessel_lube_oil_name}
                                                    </th>
                                                ))}
                                            </tr>

                                            {/* SUB HEADER */}
                                            <tr style={{ background: "#f9d0d5" }}>
                                                <th></th>
                                                <th></th>

                                                {tableOilTypes.map((oil) => (
                                                    <React.Fragment key={oil.id}>
                                                        <th className="text-center">CO</th>
                                                        <th className="text-center">BO</th>
                                                        <th className="text-center">DBK</th>
                                                        <th className="text-center">ADJ</th>
                                                        <th className="text-center">ROB</th>
                                                    </React.Fragment>
                                                ))}
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {apiData.map((row: any, index: number) => (
                                                <tr key={index}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        {row.report_name}
                                                        {" "}
                                                        ({new Date(row.report_time).toLocaleString()})
                                                    </td>

                                                    {tableOilTypes.map((oil) => {
                                                        const match = row.lube_oils?.find(
                                                            (x: any) => x.vessel_lube_oil === oil.id
                                                        );

                                                        return (
                                                            <React.Fragment key={oil.id}>
                                                                <td className="text-center">{match?.consumed ?? "-"}</td>
                                                                <td className="text-center">{match?.bunkered ?? "-"}</td>
                                                                <td className="text-center">{match?.debunkered ?? "-"}</td>
                                                                <td className="text-center">
                                                                    <b className={match?.adjusted > 0 ? "-green" : "adj-red"}>
                                                                        {match?.adjusted ?? "-"}
                                                                    </b>
                                                                </td>
                                                                <td className="text-center"><b>{match?.rob ?? "-"}</b></td>
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Row>
                            <Row className="mt-4 justify-content-end">
                                <Col md="2" className="text-end">
                                    <div className="justify_right">
                                        <button
                                            type="button"
                                            id="download"
                                            className="btn btn-primary waves-effect btn-label waves-light mb-2"
                                            onClick={exportToExcel}
                                        >
                                            <i className="mdi mdi-download label-icon" />
                                            Download
                                        </button>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </>)}
            </CardBody>
        </Card>
    );
};

export default LubeOilROBData;
